/**
 * An action's input form, generated from its JSON schema (`input_schema` in the catalogue, from
 * the action's Pydantic model). Each property becomes one field of a known kind; values may be
 * literals or {{references}} to the event, so every kind also accepts a reference.
 */
import { msg, t } from '@/lib/i18n';

export type InputKind =
  'text' | 'longtext' | 'number' | 'integer' | 'boolean' | 'enum' | 'enum-list' | 'list' | 'object' | 'id' | 'datetime' | 'recipients' | 'template';

export interface InputField {
  key: string;
  label: string;
  description: string | null;
  required: boolean;
  kind: InputKind;
  options: string[];
  min: number | null;
  max: number | null;
  default: unknown;
}

type Json = Record<string, unknown>;

/** Friendlier labels for input keys that many actions share. */
const KEY_LABEL: Record<string, string> = {
  vehicle_id: msg('Vehicle'),
  driver_id: msg('Driver'),
  asset_id: msg('Asset'),
  user_id: msg('Person'),
  detection_id: msg('Event'),
  case_id: msg('Coaching case'),
  session_id: msg('Coaching session'),
  project_id: msg('Project'),
  subject_id: msg('About (id)'),
  subject_type: msg('About (type)'),
  seconds_before: msg('Seconds before the event'),
  seconds_after: msg('Seconds after the event'),
  duration_s: msg('Length (s)'),
  channels: msg('Cameras'),
  wait_for_footage: msg('Wait until the footage arrives'),
  only_available_channels: msg('Skip cameras this vehicle does not have'),
  to: msg('To'),
  text: msg('Text'),
  subject: msg('Subject'),
  template: msg('Message template'),
  channel: msg('Channel'),
  link: msg('Link'),
  note: msg('Note'),
  reason: msg('Reason'),
  title: msg('Title'),
  body: msg('Description'),
  variables: msg('Template variables'),
  language: msg('Language'),
  include_link: msg('Include a link to view the message online'),
  send_invitation: msg('Send the invitation right away'),
  due_in_hours: msg('Due in (hours)'),
  assignee_permission: msg('For people who can'),
  assignee_user_id: msg('For this person'),
  coach_user_id: msg('Coach'),
  topics: msg('Topics'),
  detection_ids: msg('Events as evidence'),
  trip_ids: msg('Trips as evidence'),
  need: msg('Need'),
  category: msg('Category'),
};

const LONG_KEYS = new Set(['text', 'body', 'reason', 'note', 'prompt', 'invitation_text', 'description']);

function resolve(schema: Json, root: Json): Json {
  const ref = schema.$ref;
  if (typeof ref === 'string' && ref.startsWith('#/$defs/')) {
    const defs = (root.$defs ?? {}) as Record<string, Json>;
    return defs[ref.slice(8)] ?? schema;
  }
  return schema;
}

/** The non-null variants of an anyOf ("X | None" in Pydantic). */
function variants(p: Json, root: Json): Json[] {
  const any = (p.anyOf ?? p.oneOf) as Json[] | undefined;
  if (!Array.isArray(any)) return [resolve(p, root)];
  return any.map((x) => resolve(x, root)).filter((x) => x.type !== 'null');
}

function kindOf(key: string, v: Json[], actionKey: string): { kind: InputKind; options: string[] } {
  const first = v[0] ?? {};
  if (key === 'to' && actionKey === 'comms.notify') return { kind: 'recipients', options: [] };
  if (key === 'template' && actionKey.startsWith('comms.')) return { kind: 'template', options: [] };
  if (v.length > 1 && v.some((x) => x.type === 'array')) return { kind: 'list', options: [] };
  if (Array.isArray(first.enum)) return { kind: 'enum', options: first.enum.map(String) };
  switch (first.type) {
    case 'boolean':
      return { kind: 'boolean', options: [] };
    case 'integer':
      return { kind: 'integer', options: [] };
    case 'number':
      return { kind: 'number', options: [] };
    case 'array': {
      const items = (first.items ?? {}) as Json;
      if (Array.isArray(items.enum)) return { kind: 'enum-list', options: items.enum.map(String) };
      return { kind: 'list', options: [] };
    }
    case 'object':
      return { kind: 'object', options: [] };
    case 'string':
      if (first.format === 'uuid') return { kind: 'id', options: [] };
      if (first.format === 'date-time') return { kind: 'datetime', options: [] };
      if (LONG_KEYS.has(key) || (typeof first.maxLength === 'number' && first.maxLength > 300)) return { kind: 'longtext', options: [] };
      return { kind: 'text', options: [] };
    default:
      return { kind: 'text', options: [] };
  }
}

const numOr = (v: unknown): number | null => (typeof v === 'number' ? v : null);

export function inputFields(schema: Json | null | undefined, actionKey: string): InputField[] {
  if (!schema) return [];
  const props = (schema.properties ?? {}) as Record<string, Json>;
  const required = new Set(Array.isArray(schema.required) ? (schema.required as string[]) : []);
  return Object.entries(props).map(([key, p]) => {
    const v = variants(p, schema);
    const first = v[0] ?? {};
    const { kind, options } = kindOf(key, v, actionKey);
    const label = KEY_LABEL[key] ? t(KEY_LABEL[key]) : typeof p.title === 'string' ? p.title : key.replace(/_/g, ' ');
    return {
      key,
      label,
      description: typeof p.description === 'string' ? p.description : null,
      required: required.has(key),
      kind,
      options,
      min: numOr(first.minimum) ?? numOr(first.exclusiveMinimum),
      max: numOr(first.maximum),
      default: p.default,
    };
  });
}

/** Required inputs the author has not filled (after the subject fields were pre-filled). */
export function missingRequired(fields: InputField[], input: Record<string, unknown>): string[] {
  return fields
    .filter((f) => f.required)
    .filter((f) => {
      const v = input[f.key];
      return v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
    })
    .map((f) => f.key);
}
